export interface CourseraProgram {
    id: string;
    name: string;
    tagline: string;
    url: string;
    coverImage: string;
    collections: CourseraCollection[];
}

export type CourseraContentType = 'Course' | 'Specialization';

export type CourseraDifficultyLevel = 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED';

export type CourseraCourseDetailType =
    | 'deadlines'
    | 'certificate'
    | 'online'
    | 'level'
    | 'duration'
    | 'language';

export interface CourseraCourseDetail {
    type: CourseraCourseDetailType;
    title: string;
    text?: string;
}
export interface CourseraCourseInstructor {
    name: string;
    photoUrl: string;
    title: string;
    department: string;
}
export interface CourseraPartner {
    name: string;
    logoUrl?: string;
}

export interface CourseraCourse {
    id: string;
    slug: string;
    contentId: string;
    contentType: CourseraContentType;

    name: string;
    description: string;
    languageCode: string;
    difficultyLevel: CourseraDifficultyLevel | null;
    promoPhoto: string;

    new: boolean;
    rating?: {
        stars: number;
        ratings: number;
        reviews: number;
    };

    details: CourseraCourseDetail[];
    instructors: CourseraCourseInstructor[];
    partners: CourseraPartner[];

    nextSessionStart: Date | null;
    nextSessionEnd: Date | null;
}

export interface CourseraCollection {
    id: string;
    collectionId: string;
    programId: string;
    title: string;
    contents: CourseraCourse[];
}

export interface CourseraSkill {
    id: string;
    name: string;
}

export interface CourseraUpcomingSession {
    startsAt: Date;
    endsAt: Date;
    enrollmentEndsAt: Date;
}

export interface CourseraDomain {
    domainId: string;
    name: string;
}
export interface CourseraSubDomain {
    subdomainId: string;
    name: string;
}

export interface CourseraDomainType {
    domain: CourseraDomain;
    subdomain: CourseraSubDomain;
}

export interface CourseraMetadataDefinitionCourse {
    estimatedLearningTime: number;
    promoPhoto: string;

    skills: CourseraSkill[];
    upcomingSessions: CourseraUpcomingSession[];
    domainTypes: CourseraDomainType[];
}

export interface CourseraMetadataDefinitionSpecialization {
    tagline: string;
    promoPhoto: string;
    courseIds: Pick<CourseraCourse, 'id' | 'contentType'>[];
}

export interface CourseraMetadataCourse {
    typeName: 'courseMetadata';
    definition: CourseraMetadataDefinitionCourse;
}
export interface CourseraMetadataSpecialization {
    typeName: 'specializationMetadata';
    definition: CourseraMetadataDefinitionSpecialization;
}

export type GetProgramResponseContent = Pick<
    CourseraCourse,
    | 'id'
    | 'slug'
    | 'contentId'
    | 'contentType'
    | 'name'
    | 'description'
    | 'languageCode'
    | 'difficultyLevel'
    | 'partners'
    | 'instructors'
> & {
    extraMetadata: CourseraMetadataCourse | CourseraMetadataSpecialization;
};

export type GetProgramResponseCollection = Pick<
    CourseraCollection,
    'id' | 'collectionId' | 'programId' | 'title'
> & {
    contents: GetProgramResponseContent[];
};

export type GetProgramResponse = Pick<
    CourseraProgram,
    'id' | 'name' | 'tagline' | 'url' | 'coverImage'
> & {
    collections: GetProgramResponseCollection[];
};

export interface CourseraSubCategory {
    id: string;
    name: string;
}

export interface CourseraCategory {
    id: string;
    name: string;
    subCategories: CourseraSubCategory[];
}

export interface CourseraUseAccessCodeResponse {
    /** Whether the access code was granted (success) */
    granted: boolean;
    /** Whether the access code was already used by user */
    used: boolean;
    /** Whether the access code was valid */
    valid: boolean;
    /** The url to which to redirect the user to after successful access code validation */
    redirect: string;
}

export enum CourseraAccessType {
    Purchase = 'purchase',
    Code = 'code',
    Invite = 'invite',
}

export interface CourseraCheckHasAccessResponse {
    access: boolean;
    expiresAt: Date | null;
    type: CourseraAccessType | null;
    programs: {
        id: string;
        isDefault: boolean;
        name: string;
        url: string;
        coverPhoto: string | null;
    }[];
}

export interface CourseraSearchResponse {
    length: number;
    end: number;
    result: CourseraCourse[];
}
